/* AudioLearn 2026 — audio-player.js
   <audio-sample-player> web component wrapping a native <audio> element.
   States: idle, loading, playing, paused, error (+retry). Never autoplays.
   Only one player may play at a time (I-008, AC-001). */

(function () {
  'use strict';

  /* Every product sample surface resolves to one native audio element. Keeping
     these bindings here, beside the detailed player, prevents gallery controls
     from becoming a second, independently-maintained playback implementation. */
  var sharedSampleBindings = {};

  function updateSharedSampleTriggers(audio) {
    var isPlaying = Boolean(audio && !audio.paused && !audio.ended);
    if (!audio || !audio.id) return;
    document.querySelectorAll('[data-pdp-shared-sample="' + audio.id + '"]').forEach(function (button) {
      button.setAttribute('aria-pressed', String(isPlaying));
      button.removeAttribute('aria-busy');
      var label = button.querySelector('[data-pdp-direct-sample-label]');
      if (label) label.textContent = isPlaying ? 'Pause sample' : 'Listen to a sample';
      var playIcon = button.querySelector('[data-pdp-direct-sample-icon-play]');
      var pauseIcon = button.querySelector('[data-pdp-direct-sample-icon-pause]');
      if (playIcon) playIcon.hidden = isPlaying;
      if (pauseIcon) pauseIcon.hidden = !isPlaying;
    });
  }

  function bindSharedSampleAudio(audio) {
    if (!audio || !audio.id || sharedSampleBindings[audio.id]) return;
    sharedSampleBindings[audio.id] = true;
    ['playing', 'pause', 'ended', 'error'].forEach(function (eventName) {
      audio.addEventListener(eventName, function () { updateSharedSampleTriggers(audio); });
    });
    updateSharedSampleTriggers(audio);
  }

  function bindSharedSampleTriggers() {
    document.querySelectorAll('[data-pdp-shared-sample]').forEach(function (trigger) {
      var audio = document.getElementById(trigger.dataset.pdpSharedSample);
      if (!audio) return;
      bindSharedSampleAudio(audio);
      if (trigger.dataset.pdpSharedSampleBound === 'true') return;
      trigger.dataset.pdpSharedSampleBound = 'true';
      trigger.addEventListener('click', function () {
        if (audio.paused || audio.ended) {
          document.dispatchEvent(new CustomEvent('al:audio-play', { detail: { audio: audio } }));
          trigger.setAttribute('aria-busy', 'true');
          var attempt = audio.play();
          if (attempt && attempt.catch) {
            attempt.catch(function () {
              trigger.removeAttribute('aria-busy');
              updateSharedSampleTriggers(audio);
            });
          }
        } else {
          audio.pause();
        }
      });
    });
  }

  if (!window.customElements.get('audio-sample-player')) window.customElements.define('audio-sample-player', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.playerBound === 'true') return;
      this.dataset.playerBound = 'true';
      var controllerId = this.dataset.audioControllerId;
      this.audio = controllerId ? document.getElementById(controllerId) : this.querySelector('audio');
      this.playBtn = this.querySelector('[data-asp-play]');
      this.fill = this.querySelector('.asp__progress-fill');
      this.progress = this.querySelector('.asp__progress');
      this.currentEl = this.querySelector('[data-asp-current]');
      this.durationEl = this.querySelector('[data-asp-duration]');
      this.errorEl = this.querySelector('[data-asp-error]');
      this.icons = {
        play: this.querySelector('[data-asp-icon="play"]'),
        pause: this.querySelector('[data-asp-icon="pause"]'),
        loading: this.querySelector('[data-asp-icon="loading"]'),
        retry: this.querySelector('[data-asp-icon="retry"]')
      };
      if (!this.audio || !this.playBtn) return;

      bindSharedSampleAudio(this.audio);

      this.labels = {
        play: this.playBtn.dataset.labelPlay || 'Play audio sample',
        pause: this.playBtn.dataset.labelPause || 'Pause audio sample'
      };

      this.setState('idle');
      this.playBtn.addEventListener('click', this.toggle.bind(this));

      /* Keyboard: space/enter handled natively on <button>. */

      this.audio.addEventListener('timeupdate', this.render.bind(this));
      this.audio.addEventListener('loadedmetadata', this.render.bind(this));
      this.audio.addEventListener('waiting', function () { this.setState('loading'); }.bind(this));
      this.audio.addEventListener('playing', function () { this.setState('playing'); }.bind(this));
      this.audio.addEventListener('pause', function () {
        if (this.dataset.state !== 'error') this.setState(this.audio.currentTime > 0 ? 'paused' : 'idle');
      }.bind(this));
      this.audio.addEventListener('ended', function () { this.setState('paused'); }.bind(this));
      this.audio.addEventListener('error', function () { this.setState('error'); }.bind(this));

      /* seek */
      if (this.progress) {
        this.progress.addEventListener('click', function (e) {
          if (!this.audio.duration) return;
          var rect = this.progress.getBoundingClientRect();
          this.audio.currentTime = ((e.clientX - rect.left) / rect.width) * this.audio.duration;
        }.bind(this));

        /* keyboard seek (WAI-ARIA APG slider pattern): Left/Right step by 5s,
           Home/End jump to start/end. Matches click-to-seek behavior above. */
        this.progress.addEventListener('keydown', function (e) {
          if (!this.audio.duration) return;
          var step = 5;
          var next = null;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = this.audio.currentTime - step;
          else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = this.audio.currentTime + step;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = this.audio.duration;
          else return;
          e.preventDefault();
          this.audio.currentTime = Math.max(0, Math.min(this.audio.duration, next));
        }.bind(this));
      }

      /* skip buttons */
      this.querySelectorAll('[data-asp-skip]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          this.audio.currentTime = Math.max(0, Math.min((this.audio.duration || 0), this.audio.currentTime + Number(btn.dataset.aspSkip)));
        }.bind(this));
      }, this);

      /* speed */
      this.querySelectorAll('[data-asp-speed]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          this.audio.playbackRate = Number(btn.dataset.aspSpeed);
          this.querySelectorAll('[data-asp-speed]').forEach(function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
        }.bind(this));
      }, this);

      /* one player at a time */
      document.addEventListener('al:audio-play', function (e) {
        var eventAudio = e.detail && e.detail.audio;
        if (eventAudio && eventAudio !== this.audio && !this.audio.paused) this.audio.pause();
      }.bind(this));
    }

    toggle() {
      if (this.dataset.state === 'error') {
        this.setState('loading');
        this.audio.load();
        this.tryPlay();
        return;
      }
      if (this.audio.paused) this.tryPlay();
      else this.audio.pause();
    }

    tryPlay() {
      document.dispatchEvent(new CustomEvent('al:audio-play', { detail: { player: this, audio: this.audio } }));
      this.setState('loading');
      var self = this;
      var attempt = this.audio.play();
      if (attempt && attempt.catch) attempt.catch(function () { self.setState('error'); });
    }

    setState(state) {
      this.dataset.state = state;
      var isPlaying = state === 'playing';
      this.playBtn.setAttribute('aria-label', isPlaying ? this.labels.pause : this.labels.play);
      this.playBtn.dataset.error = String(state === 'error');
      Object.keys(this.icons).forEach(function (key) {
        if (!this.icons[key]) return;
        var show = (state === 'loading' && key === 'loading') ||
                   (state === 'error' && key === 'retry') ||
                   (state === 'playing' && key === 'pause') ||
                   ((state === 'idle' || state === 'paused') && key === 'play');
        this.icons[key].hidden = !show;
      }, this);
      if (this.errorEl) this.errorEl.hidden = state !== 'error';
    }

    render() {
      var d = this.audio.duration || 0;
      var t = this.audio.currentTime || 0;
      if (this.fill) this.fill.style.width = d ? (t / d) * 100 + '%' : '0%';
      if (this.currentEl) this.currentEl.textContent = format(t);
      if (this.durationEl && d) this.durationEl.textContent = format(d);
      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(Math.round(t)));
        this.progress.setAttribute('aria-valuemax', String(Math.round(d)));
        this.progress.setAttribute('aria-valuetext', format(t) + (d ? ' of ' + format(d) : ''));
      }
    }
  });

  bindSharedSampleTriggers();

  function format(seconds) {
    var m = Math.floor(seconds / 60);
    var s = String(Math.floor(seconds % 60)).padStart(2, '0');
    return m + ':' + s;
  }
})();
